import { deriveRequestNameFromUrl } from '../deriveRequestNameFromUrl';
import { defaultCollectionAuth, defaultRequestAuth } from '../requestAuth';
import { normalizeMethod } from './normalize';

/**
 * Headers injected by the browser that users don't want in their requests.
 */
const INTERNAL_HEADER_PREFIXES = [
  'host',
  'connection',
  'sec-fetch-',
  'sec-ch-',
  'cache-control',
  ':method',
  ':path',
  ':authority',
  ':scheme',
];

function isInternalHeader(name) {
  const lower = (name || '').toLowerCase();
  return INTERNAL_HEADER_PREFIXES.some((prefix) => lower === prefix || lower.startsWith(prefix));
}

/**
 * Strip the query string from a URL so params are stored separately.
 */
function stripQueryString(rawUrl) {
  try {
    const u = new URL(rawUrl);
    u.search = '';
    return u.toString();
  } catch {
    const idx = rawUrl.indexOf('?');
    return idx === -1 ? rawUrl : rawUrl.slice(0, idx);
  }
}

function parseHarBody(postData) {
  if (!postData) return { body: '', body_type: 'json' };

  const mime = String(postData.mimeType || '').toLowerCase();
  const text = postData.text || '';

  if (mime.includes('json')) {
    return { body: text, body_type: 'json' };
  }
  if (mime.includes('x-www-form-urlencoded')) {
    return { body: text, body_type: 'form' };
  }
  return { body: text, body_type: 'raw' };
}

/**
 * @param {object} json Parsed HAR JSON (with json.log.entries)
 * @param {string} [collectionName] Name for the resulting collection
 * @param {string[]} [warnings]
 * @returns {import('./types').NormalizedCollection[]}
 */
export function parseHar(json, collectionName = 'HAR Import', warnings = []) {
  const entries = json?.log?.entries;
  if (!Array.isArray(entries) || entries.length === 0) {
    warnings.push('No entries found in HAR file');
    return [
      {
        name: collectionName,
        description: '',
        settings: { collection_auth: defaultCollectionAuth() },
        requests: [],
      },
    ];
  }

  const requests = entries
    .filter((entry) => entry?.request)
    .map((entry, idx) => {
      const req = entry.request;
      const rawUrl = req.url || '';
      const baseUrl = stripQueryString(rawUrl);

      const params = (req.queryString || []).map((q) => ({
        key: String(q.name ?? ''),
        value: String(q.value ?? ''),
        enabled: true,
      }));

      const headers = (req.headers || [])
        .filter((h) => !isInternalHeader(h.name))
        .map((h) => ({
          key: String(h.name ?? ''),
          value: String(h.value ?? ''),
          enabled: true,
        }));

      const { body, body_type } = parseHarBody(req.postData);

      return {
        name: deriveRequestNameFromUrl(rawUrl) || rawUrl || 'Request',
        description: '',
        method: normalizeMethod(req.method),
        url: baseUrl,
        headers,
        params,
        cookies: [],
        body,
        body_type,
        auth: defaultRequestAuth(),
        sort_order: idx,
      };
    });

  return [
    {
      name: collectionName,
      description: '',
      settings: { collection_auth: defaultCollectionAuth() },
      requests,
    },
  ];
}
